import React, { ReactNode } from 'react';
import { useSelector } from 'react-redux';
import { styled } from '@linaria/react';
import { getSearchParameters } from 'modules/sapiSearch/selectors';
import { getNumberOfNights } from 'utils/offers';
import { Card } from '@daedalus/atlas/Card';
import ContentWrapper from '@daedalus/atlas/helpers/ContentWrapper';
import { Icon } from '@daedalus/atlas/Icon';
import { Text } from '@daedalus/atlas/Text';
import { cssTheme } from '@daedalus/atlas/themes';
import FormattedMessageWrapper from '@daedalus/core/src/localization/components/FormattedMessage';
import { roomsToConfigurationObject } from '@daedalus/core/src/room/business/roomConfiguration';
import { RoomCardVariants } from './types';
interface RoomCardVariantPropsTypes {
  variant?: RoomCardVariants;
  children: ReactNode;
}
interface CardProps {
  translationValues: {
    numberOfNights: number;
    numberOfAdults: number;
    numberOfChildren: number;
  };
  children: ReactNode;
}
const _exp = /*#__PURE__*/() => Card;
const CardWrapper = /*#__PURE__*/styled(_exp())({
  name: "CardWrapper",
  class: "c1muufsv",
  propsAsIs: true
});
const OffsetWrapper = /*#__PURE__*/styled('div')({
  name: "OffsetWrapper",
  class: "obongrv",
  propsAsIs: false
});
const _exp3 = /*#__PURE__*/() => ({
  bgColor
}) => bgColor;
const _exp4 = /*#__PURE__*/() => ({
  contentColor
}) => contentColor;
const CardLabelWrapper = /*#__PURE__*/styled('div')({
  name: "CardLabelWrapper",
  class: "c1rktqga",
  propsAsIs: false,
  vars: {
    "c1rktqga-0": [_exp3()],
    "c1rktqga-1": [_exp4()]
  }
});
const CardLabel = ({
  iconSlot,
  titleSlot,
  bgColor,
  contentColor
}: {
  iconSlot: ReactNode;
  titleSlot: ReactNode;
  bgColor: string;
  contentColor: string;
}) => <CardLabelWrapper data-id="SplitBookingCardFooterLabel" bgColor={bgColor} contentColor={contentColor}>
    {iconSlot}
    <Text variant="labelS">{titleSlot}</Text>
  </CardLabelWrapper>;
const SelectedDealCard = ({
  children,
  translationValues
}: CardProps) => <OffsetWrapper>
    <CardWrapper borderColorPath="border.brand.c200" borderRadius="lg" isOverflowHidden={false}>
      <CardLabel iconSlot={<Icon name="ArrowDown" />} titleSlot={<FormattedMessageWrapper id="splitBooking.selectedLabel" defaultMessage="Your selection for {numberOfNights, plural, one {1 night} other {# nights}}{numberOfAdults, plural, =0 {} one {, 1 adult} other {, # adults}}{numberOfChildren, plural, =0 {} one {, 1 child} other {, # children}}:" values={translationValues} />} bgColor={cssTheme.colors.background.brand.c100} contentColor={cssTheme.colors.content.brand.c800} />
      <ContentWrapper padding="s500" paddingBottom="s400">
        {children}
      </ContentWrapper>
    </CardWrapper>
  </OffsetWrapper>;
const BestDealCard = ({
  children,
  translationValues
}: CardProps) => <OffsetWrapper>
    <CardWrapper borderColorPath="border.featured.c300" borderRadius="lg" isOverflowHidden={false}>
      <CardLabel iconSlot={<Icon name="Party" />} titleSlot={<FormattedMessageWrapper id="splitBooking.bestAvailableLabel" defaultMessage="Best Available for {numberOfNights, plural, one {1 night} other {# nights}}{numberOfAdults, plural, =0 {} one {, 1 adult} other {, # adults}}{numberOfChildren, plural, =0 {} one {, 1 child} other {, # children}}:" values={translationValues} />} bgColor={cssTheme.colors.background.success.c200} contentColor={cssTheme.colors.content.brand.c900} />
      <ContentWrapper padding="s500" paddingBottom="s400">
        {children}
      </ContentWrapper>
    </CardWrapper>
  </OffsetWrapper>;
const DefaultCard = ({
  children,
  translationValues
}: CardProps) => <OffsetWrapper>
    <CardWrapper borderColorPath="border.neutral.c100" borderRadius="lg" isOverflowHidden={false}>
      <CardLabel iconSlot={<Icon name="ArrowDown" />} titleSlot={<FormattedMessageWrapper id="splitBooking.defaultLabel" defaultMessage="Price for {numberOfNights, plural, one {1 night} other {# nights}}{numberOfAdults, plural, =0 {} one {, 1 adult} other {, # adults}}{numberOfChildren, plural, =0 {} one {, 1 child} other {, # children}}:" values={translationValues} />} bgColor={cssTheme.colors.tag.neutral.background} contentColor={cssTheme.colors.tag.neutral.content} />
      <ContentWrapper padding="s500" paddingBottom="s400">
        {children}
      </ContentWrapper>
    </CardWrapper>
  </OffsetWrapper>;
export const SplitBookingFooterVariant = ({
  variant = RoomCardVariants.None,
  children
}: RoomCardVariantPropsTypes) => {
  const {
    rooms,
    checkIn,
    checkOut
  } = useSelector(getSearchParameters) || {};
  const numberOfNights = getNumberOfNights(checkIn, checkOut);
  const {
    numberOfAdults,
    numberOfChildren
  } = roomsToConfigurationObject(rooms);
  const translationValues: CardProps['translationValues'] = {
    numberOfNights,
    numberOfAdults,
    numberOfChildren
  };
  if (variant === RoomCardVariants.None) return <>{children}</>;
  if (variant === RoomCardVariants.BestDeal) {
    return <BestDealCard translationValues={translationValues}>
        {children}
      </BestDealCard>;
  }
  if (variant === RoomCardVariants.SelectedDeal) {
    return <SelectedDealCard translationValues={translationValues}>
        {children}
      </SelectedDealCard>;
  }
  return <DefaultCard translationValues={translationValues}>{children}</DefaultCard>;
};

require("./SplitBookingFooterVariant.wyw-in-js.css!=!../../../../../../../node_modules/@wyw-in-js/webpack-loader/lib/outputCssLoader.js?cacheProvider=!./SplitBookingFooterVariant.tsx");